import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FaChevronDown,
  FaChevronUp,
  FaExclamationTriangle,
  FaFileDownload,
  FaHome,
  FaMapMarkedAlt,
  FaPrint,
  FaTable,
} from "react-icons/fa";

import { formatFieldAppDateTime } from "./fieldAppTimezoneDisplay";
import FieldAppScheduleWhen from "./FieldAppScheduleWhen";
import FieldAppTelemetryMapPanel from "./FieldAppTelemetryMapPanel";
import {
  buildTelemetryScheduleFileName,
  exportTelemetrySchedulePdf,
  printTelemetrySchedule,
} from "./fieldAppTelemetryScheduleExport";
import {
  formatPanelReadingDisplay,
  resolvePanelReadingValue,
} from "./fieldAppMeterLabels";
import {
  isGroupSubFarm,
  landPlotIdsForSubFarm,
} from "../../utils/mapMeasurements";

import "./FieldAppTelemetrySchedulePanel.css";
import "./FieldAppMachineSchedulePanel.css";
import "./FieldAppTelemetryMapPanel.css";

function formatDurationFromDecimalHours(value) {
  const hours = Number(value);
  if (!Number.isFinite(hours) || hours <= 0) return "—";
  const totalMinutes = Math.round(hours * 60);
  const wholeHours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (wholeHours > 0 && minutes > 0) {
    return `${wholeHours}h ${minutes}min`;
  }
  if (wholeHours > 0) {
    return `${wholeHours}h`;
  }
  return `${minutes} min`;
}

function formatSpeed(value) {
  const speed = Number(value);
  if (!Number.isFinite(speed) || speed <= 0) return "—";
  return `${speed.toFixed(1)} km/h`;
}

function formatStoppedMinutes(value) {
  const minutes = Number(value);
  if (!Number.isFinite(minutes) || minutes <= 0) return "—";
  const wholeHours = Math.floor(minutes / 60);
  const minsRemainder = minutes - wholeHours * 60;
  const minsFormatted = minsRemainder.toLocaleString("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  if (wholeHours > 0) {
    return `${wholeHours}h ${minsFormatted} min`;
  }
  return `${minsFormatted} min`;
}

function formatStoppedPercent(value) {
  const percent = Number(value);
  if (!Number.isFinite(percent) || percent < 0) return "—";
  return `${percent.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;
}

function formatPanelReading(value, meterType) {
  return formatPanelReadingDisplay(value, meterType);
}

function formatWorkedUnits(value, meterType, gpsFallback) {
  const num = Number(value);
  if (Number.isFinite(num) && num > 0) {
    return meterType === "odometer"
      ? `${num.toLocaleString("pt-BR")} km`
      : formatDurationFromDecimalHours(num);
  }
  const gps = Number(gpsFallback);
  if (Number.isFinite(gps) && gps > 0) {
    return `${gps.toLocaleString("pt-BR")} km GPS`;
  }
  return "—";
}

function formatHectares(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return `${num.toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })} ha`;
}

function formatKm(value) {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return "—";
  return `${num.toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })} km`;
}

function formatWidthM(value) {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return "—";
  return `${num.toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  })} m`;
}

function buildSubFarmOptions(groupFarms, landplots) {
  const farms = Array.isArray(groupFarms) ? groupFarms : [];
  const plots = Array.isArray(landplots) ? landplots : [];
  return farms
    .filter(isGroupSubFarm)
    .filter(
      (farm) => landPlotIdsForSubFarm(plots, farm.id, farms).length > 0
    )
    .sort((left, right) =>
      String(left.nome_da_fazenda || left.name || "").localeCompare(
        String(right.nome_da_fazenda || right.name || ""),
        "pt-BR"
      )
    );
}

function filterLandplotsForSubFarm(allLandplots, subFarmId, groupFarms) {
  const plots = Array.isArray(allLandplots) ? allLandplots : [];
  if (!subFarmId) return plots;
  const farms = Array.isArray(groupFarms) ? groupFarms : [];
  const allowedIds = new Set(
    landPlotIdsForSubFarm(plots, subFarmId, farms).map(String)
  );
  return plots.filter((plot) => allowedIds.has(String(plot.id)));
}

const MACHINE_SCHEDULE_COLUMN_ORDER_KEY =
  "fieldApp.machineSchedule.columnOrder.v1";

const DEFAULT_MACHINE_SCHEDULE_COLUMN_IDS = [
  "date",
  "startedAt",
  "panelStart",
  "pausedAt",
  "resumedAt",
  "finishedAt",
  "panelEnd",
  "workedPanel",
  "panelHectares",
  "gpsKm",
  "telemetryHa",
  "workingWidth",
  "inMotion",
  "idleEngine",
  "totalTurno",
  "stopped",
  "stoppedPercent",
  "avgSpeed",
  "maxSpeed",
];

function readStoredMachineScheduleColumnOrder() {
  try {
    const raw = localStorage.getItem(MACHINE_SCHEDULE_COLUMN_ORDER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch (error) {
    return null;
  }
}

function normalizeMachineScheduleColumnOrder(order) {
  const saved = Array.isArray(order) ? order : [];
  const valid = saved.filter((id) =>
    DEFAULT_MACHINE_SCHEDULE_COLUMN_IDS.includes(id)
  );
  const missing = DEFAULT_MACHINE_SCHEDULE_COLUMN_IDS.filter(
    (id) => !valid.includes(id)
  );
  return [...valid, ...missing];
}

function reorderMachineScheduleColumns(order, sourceId, targetId) {
  if (!sourceId || !targetId || sourceId === targetId) {
    return order;
  }
  const next = [...order];
  const fromIndex = next.indexOf(sourceId);
  const toIndex = next.indexOf(targetId);
  if (fromIndex < 0 || toIndex < 0) {
    return order;
  }
  next.splice(fromIndex, 1);
  next.splice(toIndex, 0, sourceId);
  return next;
}

function DualLabelHeader({ screen, compact }) {
  return (
    <>
      <span className="field-app-telemetry-schedule__screen-label">{screen}</span>
      <span className="field-app-telemetry-schedule__compact-label">{compact}</span>
    </>
  );
}

function formatDayLabel(dateIso, compact) {
  if (!dateIso) return "—";
  const date = new Date(`${dateIso}T12:00:00`);
  if (Number.isNaN(date.getTime())) return dateIso;
  if (compact) {
    return date.toLocaleDateString(undefined, {
      day: "2-digit",
      month: "2-digit",
    });
  }
  return date.toLocaleDateString(undefined, {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
  });
}

function formatReportGeneratedAt() {
  return new Date().toLocaleString(undefined, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function mapCenterFromPoint(point) {
  if (!point) return null;
  const lat = Number(point.lat);
  const lng = Number(point.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (Math.abs(lat) < 0.000001 && Math.abs(lng) < 0.000001) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { lat, lng, time: point.iso || point.time || point.focusStart || null };
}

function ScheduleTimeCell({
  cell,
  dayKey,
  label,
  timezoneMode,
  onOpenMap,
  t,
  printable,
}) {
  if (!cell || !cell.iso) {
    return <span className="field-app-telemetry-schedule__empty">—</span>;
  }

  const stamp = (
    <FieldAppScheduleWhen iso={cell.iso} timezoneMode={timezoneMode} />
  );
  const canOpen = !printable && cell.focusStart && cell.focusEnd && onOpenMap;

  if (!canOpen) {
    return stamp;
  }

  return (
    <button
      type="button"
      className="field-app-telemetry-schedule__time-btn"
      title={formatFieldAppDateTime(cell.iso, timezoneMode, "card")}
      onClick={() =>
        onOpenMap({
          dayKey: dayKey || String(cell.iso).slice(0, 10),
          focusStart: cell.focusStart,
          focusEnd: cell.focusEnd,
          label,
          mapCenter: mapCenterFromPoint(cell),
          mapFullDay: true,
        })
      }
    >
      {stamp}
    </button>
  );
}

function PanelReadingCell({ cell, meterType }) {
  if (!cell) return "—";
  const reading = resolvePanelReadingValue(cell, meterType);
  const hectares =
    cell.panelHectares != null && Number.isFinite(Number(cell.panelHectares))
      ? Number(cell.panelHectares)
      : null;
  const main = formatPanelReading(reading, meterType);
  if (hectares == null) {
    return main;
  }
  return (
    <span className="field-app-machine-schedule__panel-stack">
      <span>{main}</span>
      <span className="field-app-machine-schedule__panel-ha">
        {formatHectares(hectares)}
      </span>
    </span>
  );
}

function MaxSpeedCell({ row, onOpenMap, t, printable }) {
  const point = row.maxSpeedAt;
  const label = formatSpeed(row.maxSpeedKmh);
  if (!point || !point.focusStart || !point.focusEnd || !onOpenMap || printable) {
    return <span>{label}</span>;
  }

  return (
    <button
      type="button"
      className="field-app-telemetry-schedule__time-btn"
      title={t("Abrir mapa no ponto de velocidade máxima")}
      onClick={() =>
        onOpenMap({
          dayKey: row.date,
          focusStart: point.focusStart,
          focusEnd: point.focusEnd,
          label: t("Velocidade máxima ({{speed}})", { speed: label }),
          mapCenter: mapCenterFromPoint(point),
          mapFullDay: true,
        })
      }
    >
      {label}
    </button>
  );
}

function ScheduleRowAlertDetails({ alerts, row, timezoneMode, t }) {
  if (!alerts || alerts.length === 0) return null;

  const startedIso = row.startedAt && row.startedAt.iso;
  const finishedIso = row.finishedAt && row.finishedAt.iso;
  const sessionRange =
    startedIso && finishedIso
      ? t("Turno registrado de {{start}} às {{finish}}.", {
          start: formatFieldAppDateTime(startedIso, timezoneMode, "hm"),
          finish: formatFieldAppDateTime(finishedIso, timezoneMode, "hm"),
        })
      : null;

  return (
    <div className="field-app-machine-schedule__alert-panel" role="alert">
      <strong>{t("Possível erro nos lançamentos")}</strong>
      {sessionRange ? (
        <p className="field-app-machine-schedule__alert-session">{sessionRange}</p>
      ) : null}
      <ul className="field-app-machine-schedule__alert-list">
        {alerts.map((alert, index) => (
          <li key={`${alert.kind}-${index}`}>
            <span className="field-app-machine-schedule__alert-summary">
              {alert.summary}
            </span>
            {alert.message ? (
              <p className="field-app-machine-schedule__alert-message">
                {alert.message}
              </p>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

function MachineScheduleTable({
  rows,
  meterType,
  timezoneMode,
  onOpenMap,
  t,
  printable,
  landPlotFilterActive = false,
}) {
  const [openAlertDates, setOpenAlertDates] = useState({});
  const [columnOrder, setColumnOrder] = useState(() =>
    normalizeMachineScheduleColumnOrder(readStoredMachineScheduleColumnOrder())
  );
  const [dragColumnId, setDragColumnId] = useState(null);
  const [dragOverColumnId, setDragOverColumnId] = useState(null);

  const toggleRowAlert = useCallback((dateKey) => {
    setOpenAlertDates((current) => ({
      ...current,
      [dateKey]: !current[dateKey],
    }));
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(
        MACHINE_SCHEDULE_COLUMN_ORDER_KEY,
        JSON.stringify(columnOrder)
      );
    } catch (error) {
      // ignore storage failures
    }
  }, [columnOrder]);

  const panelLabel =
    meterType === "odometer"
      ? t("Hodômetro")
      : meterType === "both"
        ? t("Painel (h/km)")
        : t("Horímetro");
  const workedLabel =
    meterType === "odometer" ? t("Km percorridos") : t("Horas painel");

  const columnsById = useMemo(() => {
    const cols = {
      date: {
        id: "date",
        renderHeader: () => t("Dia"),
        renderCell: (row) => (
          <div className="field-app-machine-schedule__date-cell">
            <span>{formatDayLabel(row.date, printable)}</span>
            {!printable && row.alerts && row.alerts.length > 0 ? (
              <button
                type="button"
                className="field-app-machine-schedule__alert-toggle"
                title={t("Ver alerta de inconsistência neste dia")}
                aria-expanded={Boolean(openAlertDates[row.date])}
                aria-label={t("Ver alerta de inconsistência neste dia")}
                onClick={() => toggleRowAlert(row.date)}
              >
                <FaExclamationTriangle aria-hidden="true" />
              </button>
            ) : null}
          </div>
        ),
        cellClassName: "field-app-telemetry-schedule__date",
      },
      startedAt: {
        id: "startedAt",
        renderHeader: () => t("Início"),
        renderCell: (row) => (
          <ScheduleTimeCell
            cell={row.startedAt}
            dayKey={row.date}
            label={t("Início")}
            timezoneMode={timezoneMode}
            onOpenMap={onOpenMap}
            printable={printable}
            t={t}
          />
        ),
      },
      panelStart: {
        id: "panelStart",
        renderHeader: () => panelLabel,
        renderCell: (row) => (
          <PanelReadingCell cell={row.startedAt} meterType={meterType} />
        ),
        cellClassName: "field-app-machine-schedule__panel-reading",
      },
      pausedAt: {
        id: "pausedAt",
        renderHeader: () => t("Pausa"),
        renderCell: (row) => (
          <ScheduleTimeCell
            cell={row.pausedAt}
            dayKey={row.date}
            label={t("Pausa")}
            timezoneMode={timezoneMode}
            onOpenMap={onOpenMap}
            printable={printable}
            t={t}
          />
        ),
      },
      resumedAt: {
        id: "resumedAt",
        renderHeader: () => t("Retorno"),
        renderCell: (row) => (
          <ScheduleTimeCell
            cell={row.resumedAt}
            dayKey={row.date}
            label={t("Retorno")}
            timezoneMode={timezoneMode}
            onOpenMap={onOpenMap}
            printable={printable}
            t={t}
          />
        ),
      },
      finishedAt: {
        id: "finishedAt",
        renderHeader: () => t("Encerramento"),
        renderCell: (row) => (
          <ScheduleTimeCell
            cell={row.finishedAt}
            dayKey={row.date}
            label={t("Encerramento")}
            timezoneMode={timezoneMode}
            onOpenMap={onOpenMap}
            printable={printable}
            t={t}
          />
        ),
      },
      panelEnd: {
        id: "panelEnd",
        renderHeader: () => panelLabel,
        renderCell: (row) => (
          <PanelReadingCell cell={row.finishedAt} meterType={meterType} />
        ),
        cellClassName: "field-app-machine-schedule__panel-reading",
      },
      workedPanel: {
        id: "workedPanel",
        renderHeader: () => workedLabel,
        renderCell: (row) =>
          formatWorkedUnits(row.workedPanelUnits, meterType, row.gpsDistanceKm),
        cellClassName: "field-app-telemetry-schedule__totals",
      },
      panelHectares: {
        id: "panelHectares",
        renderHeader: () => (
          <DualLabelHeader screen={t("Hectares painel")} compact={t("Ha painel")} />
        ),
        renderCell: (row) => formatHectares(row.workedPanelHectares),
        cellClassName: "field-app-telemetry-schedule__totals",
      },
      gpsKm: {
        id: "gpsKm",
        renderHeader: () => (
          <DualLabelHeader
            screen={
              <>
                {t("Km percorridos")}
                <br />
                {t("(telemetria)")}
              </>
            }
            compact={t("Km GPS")}
          />
        ),
        renderCell: (row) => formatKm(row.gpsDistanceKm),
      },
      telemetryHa: {
        id: "telemetryHa",
        renderHeader: () => (
          <DualLabelHeader
            screen={
              <>
                {t("Hectares")}
                <br />
                {t("(telemetria)")}
              </>
            }
            compact={t("Ha GPS")}
          />
        ),
        renderCell: (row) => formatHectares(row.telemetryAreaHa),
      },
      workingWidth: {
        id: "workingWidth",
        renderHeader: () => (
          <DualLabelHeader
            screen={
              <>
                {t("Largura")}
                <br />
                {t("da máquina")}
              </>
            }
            compact={t("Larg.")}
          />
        ),
        renderCell: (row) => formatWidthM(row.workingWidthM),
      },
      inMotion: {
        id: "inMotion",
        renderHeader: () => (
          <DualLabelHeader screen={t("Em movimento")} compact={t("Mov.")} />
        ),
        renderCell: (row) => formatStoppedMinutes(row.inMotionMinutes),
      },
      idleEngine: {
        id: "idleEngine",
        renderHeader: () => (
          <DualLabelHeader
            screen={t("Tempo da máquina parada com motor ligado")}
            compact={t("Parada c/ motor")}
          />
        ),
        renderCell: (row) => formatStoppedMinutes(row.idleEngineMinutes),
      },
      totalTurno: {
        id: "totalTurno",
        renderHeader: () => (
          <DualLabelHeader
            screen={t("Tempo total (início ao encerramento)")}
            compact={t("Total turno")}
          />
        ),
        renderCell: (row) => formatDurationFromDecimalHours(row.totalHours),
        cellClassName: "field-app-telemetry-schedule__totals",
      },
      stopped: {
        id: "stopped",
        renderHeader: () => t("Parado"),
        renderCell: (row) => formatStoppedMinutes(row.stoppedMinutes),
      },
      stoppedPercent: {
        id: "stoppedPercent",
        renderHeader: () => (
          <DualLabelHeader
            screen={
              <>
                {t("Percentual da máquina parada")}
                <br />
                {t("com motor ligado")}
              </>
            }
            compact={t("% c/ motor")}
          />
        ),
        renderCell: (row) => formatStoppedPercent(row.stoppedPercent),
      },
      avgSpeed: {
        id: "avgSpeed",
        renderHeader: () => t("V. méd."),
        renderCell: (row) => formatSpeed(row.avgSpeedKmh),
      },
      maxSpeed: {
        id: "maxSpeed",
        renderHeader: () => t("V. máx."),
        renderCell: (row) => (
          <MaxSpeedCell
            row={row}
            onOpenMap={onOpenMap}
            printable={printable}
            t={t}
          />
        ),
      },
    };
    return cols;
  }, [
    meterType,
    onOpenMap,
    openAlertDates,
    panelLabel,
    printable,
    t,
    timezoneMode,
    toggleRowAlert,
    workedLabel,
  ]);

  const orderedColumns = useMemo(
    () =>
      columnOrder
        .map((columnId) => columnsById[columnId])
        .filter((column) => Boolean(column)),
    [columnOrder, columnsById]
  );

  const columnCount = orderedColumns.length;

  const handleDragStart = useCallback(
    (columnId) => (event) => {
      if (printable) return;
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", columnId);
      setDragColumnId(columnId);
    },
    [printable]
  );

  const handleDragOver = useCallback(
    (columnId) => (event) => {
      if (printable) return;
      event.preventDefault();
      setDragOverColumnId(columnId);
    },
    [printable]
  );

  const handleDrop = useCallback(
    (targetColumnId) => (event) => {
      if (printable) return;
      event.preventDefault();
      const sourceColumnId =
        event.dataTransfer.getData("text/plain") || dragColumnId;
      if (!sourceColumnId) return;
      setColumnOrder((current) =>
        reorderMachineScheduleColumns(current, sourceColumnId, targetColumnId)
      );
      setDragColumnId(null);
      setDragOverColumnId(null);
    },
    [dragColumnId, printable]
  );

  const handleDragEnd = useCallback(() => {
    setDragColumnId(null);
    setDragOverColumnId(null);
  }, []);

  return (
    <table className="field-app-telemetry-schedule__table field-app-machine-schedule__table">
      <thead>
        <tr>
          {orderedColumns.map((column) => {
            const isDragging = dragColumnId === column.id;
            const isDragOver = dragOverColumnId === column.id;
            const thClassName = [
              !printable ? "field-app-machine-schedule__th--draggable" : "",
              isDragging ? "field-app-machine-schedule__th--dragging" : "",
              isDragOver ? "field-app-machine-schedule__th--drag-over" : "",
            ]
              .filter(Boolean)
              .join(" ");

            return (
              <th
                key={column.id}
                className={thClassName || undefined}
                draggable={!printable}
                onDragStart={handleDragStart(column.id)}
                onDragOver={handleDragOver(column.id)}
                onDrop={handleDrop(column.id)}
                onDragEnd={handleDragEnd}
                title={
                  printable
                    ? undefined
                    : t("Arraste para reordenar esta coluna")
                }
              >
                {column.renderHeader()}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const hasData =
            row.startedAt ||
            row.pausedAt ||
            row.resumedAt ||
            row.finishedAt ||
            (Number(row.totalHours) || 0) > 0 ||
            (Number(row.gpsDistanceKm) || 0) > 0;

          if (!hasData) {
            return (
              <tr key={row.date} className="field-app-telemetry-schedule__empty-row">
                <td className="field-app-telemetry-schedule__date">
                  {formatDayLabel(row.date, printable)}
                </td>
                <td colSpan={columnCount - 1}>
                  {landPlotFilterActive
                    ? t("Sem uso neste talhão neste dia")
                    : t("Sem uso registrado neste dia")}
                </td>
              </tr>
            );
          }

          return (
            <React.Fragment key={row.date}>
              <tr
                className={
                  row.alerts && row.alerts.length > 0
                    ? "field-app-machine-schedule__row--alert"
                    : undefined
                }
              >
                {orderedColumns.map((column) => (
                  <td
                    key={`${row.date}-${column.id}`}
                    className={column.cellClassName || undefined}
                  >
                    {column.renderCell(row)}
                  </td>
                ))}
              </tr>
              {!printable &&
              row.alerts &&
              row.alerts.length > 0 &&
              openAlertDates[row.date] ? (
                <tr className="field-app-machine-schedule__alert-row">
                  <td colSpan={columnCount}>
                    <ScheduleRowAlertDetails
                      alerts={row.alerts}
                      row={row}
                      timezoneMode={timezoneMode}
                      t={t}
                    />
                  </td>
                </tr>
              ) : null}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

export default function FieldAppMachineSchedulePanel({
  expanded,
  onToggleExpanded,
  rows,
  meterType,
  loading,
  error,
  timezoneMode,
  farmId,
  selection,
  machineLabel,
  machineOptions = [],
  onMachineChange,
  landplots = [],
  groupFarms = [],
  selectedSubFarmId = null,
  onSubFarmChange,
  selectedLandplotId = null,
  selectedLandplotName = "",
  onLandplotChange,
  periodLabel,
  operatorLabel,
  hideToggle = false,
  t,
}) {
  const [mapView, setMapView] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const printAreaRef = useRef(null);
  const list = Array.isArray(rows) ? rows : [];
  const landPlotFilterActive = Boolean(selectedLandplotId);
  const subFarmOptions = useMemo(
    () => buildSubFarmOptions(groupFarms, landplots),
    [groupFarms, landplots]
  );
  const filteredLandplots = useMemo(
    () =>
      filterLandplotsForSubFarm(landplots, selectedSubFarmId, groupFarms),
    [groupFarms, landplots, selectedSubFarmId]
  );
  const sortedLandplots = useMemo(() => {
    return [...filteredLandplots].sort((left, right) =>
      String(left.name || "").localeCompare(String(right.name || ""), "pt-BR")
    );
  }, [filteredLandplots]);
  const activeSubFarmName = useMemo(() => {
    if (!selectedSubFarmId) return "";
    const match = subFarmOptions.find(
      (farm) => Number(farm.id) === Number(selectedSubFarmId)
    );
    return match ? match.nome_da_fazenda || match.name || "" : "";
  }, [selectedSubFarmId, subFarmOptions]);
  const activeLandplotName = useMemo(() => {
    if (selectedLandplotName) return selectedLandplotName;
    if (!selectedLandplotId) return "";
    const match = sortedLandplots.find(
      (plot) => Number(plot.id) === Number(selectedLandplotId)
    );
    return match ? match.name : "";
  }, [selectedLandplotId, selectedLandplotName, sortedLandplots]);

  const handleOpenMap = useCallback((payload) => {
    setMapView({ ...payload, token: Date.now() });
  }, []);

  const handlePrint = useCallback(() => {
    printTelemetrySchedule();
  }, []);

  const handleExportPdf = useCallback(async () => {
    if (!printAreaRef.current || exportingPdf) return;
    setExportingPdf(true);
    try {
      await exportTelemetrySchedulePdf(
        printAreaRef.current,
        buildTelemetryScheduleFileName({
          operatorLabel,
          periodLabel: `${machineLabel || "maquina"}-${periodLabel || ""}`,
        })
      );
    } finally {
      setExportingPdf(false);
    }
  }, [exportingPdf, machineLabel, operatorLabel, periodLabel]);

  return (
    <div className="field-app-machine-schedule">
      <div className="field-app-machine-schedule__header">
        {hideToggle ? (
          <span className="field-app-machine-schedule__title">
            <FaTable aria-hidden="true" />
            <span>{t("Planilha da máquina")}</span>
          </span>
        ) : (
          <button
            type="button"
            className="field-app-machine-schedule__toggle btn btn-default"
            onClick={onToggleExpanded}
            aria-expanded={expanded}
          >
            <FaTable aria-hidden="true" />
            <span>{t("Planilha da máquina")}</span>
            {expanded ? <FaChevronUp aria-hidden="true" /> : <FaChevronDown aria-hidden="true" />}
          </button>
        )}
        {machineOptions.length > 1 && onMachineChange ? (
          <label className="field-app-machine-schedule__machine-picker">
            <span>{t("Máquina")}</span>
            <select
              value={machineLabel || machineOptions[0]}
              onChange={(event) => onMachineChange(event.target.value)}
            >
              {machineOptions.map((ident) => (
                <option key={ident} value={ident}>
                  {ident}
                </option>
              ))}
            </select>
          </label>
        ) : machineLabel ? (
          <span className="field-app-machine-schedule__machine-name">{machineLabel}</span>
        ) : null}
        {subFarmOptions.length > 0 && onSubFarmChange ? (
          <label className="field-app-machine-schedule__subfarm-picker">
            <FaHome
              aria-hidden="true"
              className="field-app-machine-schedule__subfarm-icon"
              title={t("Filtrar talhões por sub-fazenda")}
            />
            <select
              value={selectedSubFarmId || ""}
              onChange={(event) => {
                const value = event.target.value;
                onSubFarmChange(value ? Number(value) : null);
              }}
              aria-label={t("Filtrar talhões por sub-fazenda")}
              title={t("Filtrar talhões por sub-fazenda")}
            >
              <option value="">{t("Todas as sub-fazendas")}</option>
              {subFarmOptions.map((farm) => (
                <option key={farm.id} value={farm.id}>
                  {farm.nome_da_fazenda || farm.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {sortedLandplots.length > 0 && onLandplotChange ? (
          <label className="field-app-machine-schedule__landplot-picker">
            <FaMapMarkedAlt
              aria-hidden="true"
              className="field-app-machine-schedule__landplot-icon"
              title={t("Filtrar telemetria por talhão")}
            />
            <select
              value={selectedLandplotId || ""}
              onChange={(event) => {
                const value = event.target.value;
                onLandplotChange(value ? Number(value) : null);
              }}
              disabled={subFarmOptions.length > 0 && !selectedSubFarmId}
              aria-label={t("Filtrar telemetria por talhão")}
              title={
                subFarmOptions.length > 0 && !selectedSubFarmId
                  ? t("Selecione uma sub-fazenda primeiro")
                  : t("Filtrar telemetria por talhão")
              }
            >
              <option value="">
                {selectedSubFarmId
                  ? t("Todos os talhões da sub-fazenda")
                  : t("Todos os talhões")}
              </option>
              {sortedLandplots.map((plot) => (
                <option key={plot.id} value={plot.id}>
                  {plot.name}
                  {plot.area_h2 ? ` — ${plot.area_h2} ha` : ""}
                </option>
              ))}
            </select>
          </label>
        ) : subFarmOptions.length > 0 && selectedSubFarmId && onLandplotChange ? (
          <span className="field-app-machine-schedule__landplot-empty">
            {t("Nenhum talhão nesta sub-fazenda")}
          </span>
        ) : null}
      </div>

      {expanded ? (
        <div className="field-app-machine-schedule__body field-app-telemetry-schedule">
          {loading ? (
            <p className="field-app-telemetry-schedule__hint">
              {t("Carregando planilha da máquina…")}
            </p>
          ) : null}
          {error ? (
            <p className="field-app-machine-schedule__error">{error}</p>
          ) : null}
          {!loading && !error && !list.length ? (
            <p className="field-app-telemetry-schedule__hint">
              {t("Nenhum registro da máquina no período selecionado.")}
            </p>
          ) : null}

          {!loading && !error && list.length ? (
            <>
              <div className="field-app-telemetry-schedule__toolbar">
                <div className="field-app-telemetry-schedule__toolbar-group">
                  <button
                    type="button"
                    className="btn btn-default field-app-telemetry-schedule__toolbar-btn"
                    onClick={handlePrint}
                    aria-label={t("Imprimir planilha em A4 paisagem")}
                    title={t("Imprimir planilha em A4 paisagem")}
                  >
                    <FaPrint aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="btn btn-default field-app-telemetry-schedule__toolbar-btn"
                    onClick={handleExportPdf}
                    disabled={exportingPdf}
                    aria-label={t("Salvar planilha em PDF (A4 paisagem)")}
                    title={
                      exportingPdf
                        ? t("Gerando PDF…")
                        : t("Salvar planilha em PDF (A4 paisagem)")
                    }
                  >
                    <FaFileDownload aria-hidden="true" />
                  </button>
                </div>
              </div>

              <p className="field-app-telemetry-schedule__hint field-app-telemetry-schedule__screen-only">
                {landPlotFilterActive && activeLandplotName
                  ? t(
                      "Filtrando telemetria pelo talhão {{name}}. Horas painel e leituras do operador permanecem do turno completo; km, hectares, parado e velocidades usam apenas pontos GPS dentro do talhão. Arraste os cabeçalhos das colunas para reordenar; a ordem fica salva neste navegador.",
                      { name: activeLandplotName }
                    )
                  : t(
                      "Horários em hora local. Leituras do painel são as informadas pelo operador ao assumir, pausar ou finalizar. Parado com motor ligado: horas painel − (tempo total turno − parado GPS). % = parado com motor ligado ÷ horas painel (ex.: 41 ÷ 72 = 56,94%). Parado: paradas ≥ 5 min pelo GPS. Arraste os cabeçalhos das colunas para reordenar; a ordem fica salva neste navegador."
                    )}
              </p>

              <div
                ref={printAreaRef}
                className="field-app-telemetry-schedule__print-area"
              >
                <div className="field-app-telemetry-schedule__print-header">
                  <h2 className="field-app-telemetry-schedule__print-title">
                    {t("Planilha da máquina")}
                  </h2>
                  <div className="field-app-telemetry-schedule__print-meta">
                    {machineLabel ? (
                      <span>
                        <strong>{t("Máquina")}:</strong> {machineLabel}
                      </span>
                    ) : null}
                    {operatorLabel ? (
                      <span>
                        <strong>{t("Operador")}:</strong> {operatorLabel}
                      </span>
                    ) : null}
                    {periodLabel ? (
                      <span>
                        <strong>{t("Período")}:</strong> {periodLabel}
                      </span>
                    ) : null}
                    {landPlotFilterActive && activeLandplotName ? (
                      <span>
                        <strong>{t("Talhão")}:</strong> {activeLandplotName}
                      </span>
                    ) : null}
                    {selectedSubFarmId && activeSubFarmName ? (
                      <span>
                        <strong>{t("Sub-fazenda")}:</strong> {activeSubFarmName}
                      </span>
                    ) : null}
                    <span>
                      <strong>{t("Gerado em")}:</strong> {formatReportGeneratedAt()}
                    </span>
                  </div>
                </div>

                <div className="field-app-telemetry-schedule__wrap">
                  <MachineScheduleTable
                    rows={list}
                    meterType={meterType}
                    timezoneMode={timezoneMode}
                    onOpenMap={handleOpenMap}
                    t={t}
                    printable={false}
                    landPlotFilterActive={landPlotFilterActive}
                  />
                </div>
              </div>

              {mapView ? (
                <FieldAppTelemetryMapPanel
                  farmId={farmId}
                  selection={selection}
                  view={mapView}
                  onClose={() => setMapView(null)}
                  t={t}
                />
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
